import {
  DeleteOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Flex, Image, Popconfirm, Spin, Typography, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import CameraCapture from './CameraCapture';
import {
  getPhotoListByScoreQueryKey,
  getSubscriptionUsageQueryKey,
  usePhotoConfirm,
  usePhotoListByScore,
  usePhotoPresign,
  usePhotoRemove,
} from '../../../libs/ohat-api';
import { getApiErrorMessage, isLimitExceeded } from '../../../utils/apiError';
import { useTranslation } from 'react-i18next';

// バックエンド（api/src/modules/photo/dto/photo.dto.ts）と揃える。
// JPEGへの正規化はサーバ側（confirm時）で行うため、ここでは元ファイルをそのまま送る。
const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_PHOTOS_PER_SCORE = 5;

type Props = {
  ohatScoreId: string;
};

/**
 * 評価に紐づく口腔内写真の一覧・アップロード・撮影・削除を扱うセクション。
 *
 * アップロードは presign → S3への直PUT → confirm の3段階。
 * S3へのPUTは署名付きURLを使うため、認証ヘッダを付けない素の fetch で行う。
 */
export default function OhatPhotoSection({ ohatScoreId }: Props) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: photos, isLoading } = usePhotoListByScore(ohatScoreId);
  const { mutateAsync: presign } = usePhotoPresign();
  const { mutateAsync: confirm } = usePhotoConfirm();
  const { mutateAsync: remove } = usePhotoRemove();

  // アップロード中の枚数（上限チェックとUIロックに使用）
  const [pending, setPending] = useState(0);
  // カメラ撮影モーダルの表示状態
  const [cameraOpen, setCameraOpen] = useState(false);
  // ブラウザがカメラをサポートしているか（HTTPS + mediaDevices が存在する場合のみ true）
  const [hasCamera, setHasCamera] = useState(false);
  const inFlightRef = useRef(0);

  // カメラの有無を初回マウント時に判定
  const checkCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setHasCamera(devices.some((d) => d.kind === 'videoinput'));
    } catch {
      // カメラ判定に失敗した場合はボタンを表示しない
    }
  }, []);

  useEffect(() => {
    void checkCamera();
  }, [checkCamera]);

  const invalidateList = () => {
    queryClient.invalidateQueries({
      queryKey: getPhotoListByScoreQueryKey(ohatScoreId),
    });
    // 月間アップロード数の使用量表示（プラン管理画面など）も更新する
    queryClient.invalidateQueries({
      queryKey: getSubscriptionUsageQueryKey(),
    });
  };

  const uploadedCount = photos?.length ?? 0;

  const uploadOne = async (file: File) => {
    // クライアント側バリデーション（サーバでも再チェックされる）
    if (!ALLOWED_CONTENT_TYPES.includes(file.type)) {
      message.error(t('photo.unsupportedType'));
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      message.error(t('photo.tooLarge'));
      return;
    }

    inFlightRef.current += 1;
    setPending((n) => n + 1);
    try {
      // 1. 署名付きURLを発行（上限チェックのみ）
      const { uploadUrl, s3Key } = await presign({
        data: {
          ohatScoreId,
          contentType: file.type,
          sizeBytes: file.size,
        },
      });

      // 2. S3へ直接PUT（認証ヘッダを付けない素のfetch）
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) {
        throw new Error(`S3へのアップロードに失敗しました（${res.status}）`);
      }

      // 3. アップロードを確定（サーバ側でJPEGへ正規化・使用量カウント）
      await confirm({
        data: {
          ohatScoreId,
          s3Key,
          contentType: file.type,
          sizeBytes: file.size,
        },
      });
    } catch (e) {
      const fallback = isLimitExceeded(e)
        ? t('photo.planLimit')
        : t('photo.uploadFailed');
      message.error(getApiErrorMessage(e, fallback));
    } finally {
      inFlightRef.current -= 1;
      setPending((n) => n - 1);
    }
  };

  // antd Upload に自動送信させず、手動で presign→PUT→confirm を回す
  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const remaining = MAX_PHOTOS_PER_SCORE - uploadedCount - inFlightRef.current;
    if (remaining <= 0) {
      message.warning(t('photo.maxPerScore', { max: MAX_PHOTOS_PER_SCORE }));
      return Upload.LIST_IGNORE;
    }
    void uploadOne(file).then(invalidateList);
    return false; // 自動アップロードを止める
  };

  const onDelete = async (id: string) => {
    try {
      await remove({ id });
      await invalidateList();
      message.success(t('photo.deleted'));
    } catch (e) {
      message.error(getApiErrorMessage(e, t('photo.deleteFailed')));
    }
  };

  const reachedLimit = uploadedCount + pending >= MAX_PHOTOS_PER_SCORE;

  /** カメラ撮影で得た File をアップロードする */
  const onCameraCapture = (file: File) => {
    const remaining = MAX_PHOTOS_PER_SCORE - uploadedCount - inFlightRef.current;
    if (remaining <= 0) {
      message.warning(t('photo.maxPerScore', { max: MAX_PHOTOS_PER_SCORE }));
      return;
    }
    void uploadOne(file).then(invalidateList);
  };

  // accept は image/* にしておくと、モバイルの標準ピッカーが
  // 「写真を撮る」と「フォトライブラリ」の両方を提示する（＝撮影/選択を1ボタンに統合）。
  const uploadProps: UploadProps = {
    accept: 'image/*',
    multiple: true,
    showUploadList: false,
    beforeUpload,
    disabled: reachedLimit,
  };

  return (
    <>
    <Card
      title={
        <Flex align="center" gap={8}>
          <PictureOutlined />
          <span>{t('photo.title')}</span>
          <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
            （{uploadedCount}/{MAX_PHOTOS_PER_SCORE}）
          </Typography.Text>
        </Flex>
      }
    >
      <Flex
        gap={12}
        wrap="wrap"
        align="center"
        justify="center"
        style={{ marginBottom: 16 }}
      >
        <Upload {...uploadProps}>
          <Button
            type="primary"
            size="large"
            shape="round"
            icon={<PictureOutlined />}
            disabled={reachedLimit}
          >
            {t('photo.add')}
          </Button>
        </Upload>
        {hasCamera && (
          <Button
            size="large"
            shape="round"
            icon={<VideoCameraOutlined />}
            disabled={reachedLimit}
            onClick={() => setCameraOpen(true)}
          >
            {t('photo.capture')}
          </Button>
        )}
        {pending > 0 && (
          <Flex align="center" gap={8}>
            <Spin size="small" />
            <Typography.Text type="secondary">
              {t('photo.uploading', { count: pending })}
            </Typography.Text>
          </Flex>
        )}
      </Flex>

      {isLoading ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : uploadedCount === 0 && pending === 0 ? (
        <Typography.Text type="secondary">{t('photo.empty')}</Typography.Text>
      ) : (
        <Image.PreviewGroup>
          <Flex gap={12} wrap="wrap">
            {photos?.map((photo) => (
              <div key={photo.id} style={{ position: 'relative' }}>
                <Image
                  src={photo.url}
                  alt={t('photo.title')}
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
                <Popconfirm
                  title={t('common.deleteConfirm')}
                  onConfirm={() => onDelete(photo.id)}
                  okText={t('common.submit') || 'Yes'}
                  cancelText={t('common.cancel') || 'No'}
                >
                  <Button
                    danger
                    size="small"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 4, right: 4 }}
                  />
                </Popconfirm>
              </div>
            ))}
          </Flex>
        </Image.PreviewGroup>
      )}
    </Card>

      {/* カメラ撮影モーダル */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={onCameraCapture}
        disabled={reachedLimit}
      />
    </>
  );
}
